import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { CheckCircle2, ArrowLeft, ArrowRight, Sparkles, Send, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { usePageMeta } from '../hooks/usePageMeta';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.1 }
  }
};

// Form Field Component with animation
const FormField = ({ label, required, children, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, x: -20 }}
    animate={{ opacity: 1, x: 0 }}
    transition={{ duration: 0.4, delay }}
  >
    <Label className="text-gray-700 font-medium">
      {label} {required && <span className="text-red-500">*</span>}
    </Label>
    <div className="mt-1.5">{children}</div>
  </motion.div>
);

const RequestPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [submitting, setSubmitting] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  
  usePageMeta({
    title: 'Start Your Project | BM-Assist.co',
    description: 'Request a custom marketing project for your small business. Get digital setup, local ads, print materials, or social media help.',
    ogTitle: 'Start Your Project | BM-Assist.co',
    ogDescription: 'Start your marketing project today. No monthly retainers, just affordable one-time solutions.'
  });

  const [formData, setFormData] = useState({
    form_type: 'Client Project',
    full_name: '',
    email: '',
    phone: '',
    business_name: '',
    business_type: '',
    project_package: '',
    project_type_notes: '',
    request_category: 'full_project',
    main_goal: '',
    budget: '',
    timeline: '',
    current_links: '',
    ad_accounts: '',
    notes: '',
    source: 'request-page'
  });

  useEffect(() => {
    const projectParam = searchParams.get('project');
    if (projectParam) {
      const decodedProject = decodeURIComponent(projectParam);
      const packageOptions = [
        'Digital Setup',
        'Local Ad Boost',
        'Offline Essentials Kit',
        'Promo & Social Starter'
      ];
      
      if (packageOptions.includes(decodedProject)) {
        setFormData(prev => ({
          ...prev,
          project_package: decodedProject
        }));
      }
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.project_package) {
      toast.error('Please select a package');
      return;
    }
    
    setSubmitting(true);

    try {
      const payload = {
        ...formData,
        submitted_at: new Date().toISOString()
      };

      await axios.post(`${API}/project-requests`, payload);
      toast.success('Request submitted successfully!');
      navigate('/submitted-project');
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const packages = [
    { value: 'Digital Setup', label: 'Digital Setup — from $299', description: 'Google Business + Facebook setup' },
    { value: 'Local Ad Boost', label: 'Local Ad Boost — from $499', description: '2-week ad campaign' },
    { value: 'Offline Essentials Kit', label: 'Offline Essentials Kit — from $249', description: 'Business cards + print materials' },
    { value: 'Promo & Social Starter', label: 'Promo & Social Starter — from $399', description: 'Social graphics + content' },
    { value: 'Custom / Other', label: 'Custom / Other', description: 'Tell us what you need' }
  ];

  const businessTypes = [
    'Construction / General Contractor',
    'Lawn Care / Landscaping',
    'Plumbing',
    'HVAC',
    'Electrical',
    'Roofing',
    'Painting',
    'Cleaning Services',
    'Handyman',
    'Other Home Services',
    'Food Truck / Mobile Food',
    'Restaurant / Cafe',
    'Salon / Barbershop',
    'Auto Services',
    'Other'
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Navigation */}
      <motion.nav 
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
        className="fixed top-0 w-full bg-white/90 backdrop-blur-md border-b border-gray-100 z-50"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="text-2xl font-bold bg-gradient-to-r from-[#1E3A8A] to-[#3B82F6] bg-clip-text text-transparent">
              BM-Assist.co
            </Link>
            <Link to="/">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button variant="outline" className="border-[#1E3A8A] text-[#1E3A8A] hover:bg-[#1E3A8A] hover:text-white rounded-full">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Home
                </Button>
              </motion.div>
            </Link>
          </div>
        </div>
      </motion.nav>

      {/* Hero Section */}
      <section className="pt-32 pb-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        {/* Background decoration */}
        <motion.div
          className="absolute top-20 right-0 w-96 h-96 bg-blue-100 rounded-full filter blur-3xl opacity-40"
          animate={{ x: [0, 30, 0], y: [0, -20, 0] }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute bottom-0 left-0 w-72 h-72 bg-amber-100 rounded-full filter blur-3xl opacity-40"
          animate={{ x: [0, -20, 0], y: [0, 20, 0] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        />

        <div className="max-w-4xl mx-auto text-center relative z-10">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            <motion.div
              variants={fadeInUp}
              className="inline-flex items-center gap-2 bg-[#1E3A8A]/10 text-[#1E3A8A] px-4 py-2 rounded-full text-sm font-semibold mb-6"
            >
              <Sparkles className="w-4 h-4" />
              Start Your Project
            </motion.div>
            
            <motion.h1 
              variants={fadeInUp}
              className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight"
            >
              <span className="text-gray-900">Tell us what you </span>
              <span className="bg-gradient-to-r from-[#1E3A8A] to-[#3B82F6] bg-clip-text text-transparent">
                need
              </span>
            </motion.h1>
            
            <motion.p 
              variants={fadeInUp}
              className="text-xl text-gray-600 max-w-2xl mx-auto mb-8"
            >
              Print, digital, ads, or setup — fill out this form and we'll scope your project.
            </motion.p>

            <motion.div 
              variants={fadeInUp}
              className="flex flex-wrap justify-center gap-4 text-sm"
            >
              {['Made for local businesses', 'One-time projects', 'No monthly retainers'].map((item, index) => (
                <motion.div
                  key={item}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.5 + index * 0.1 }}
                  className="flex items-center gap-2 bg-white/80 backdrop-blur-sm px-4 py-2 rounded-full shadow-sm"
                >
                  <CheckCircle2 className="w-4 h-4 text-green-500" />
                  <span className="text-gray-700">{item}</span>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="max-w-2xl mx-auto"
        >
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Package Selection */}
              <FormField label="What package are you interested in?" required delay={0}>
                <div className="grid gap-3 mt-2">
                  {packages.map((pkg, index) => (
                    <motion.label
                      key={pkg.value}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.1 * index }}
                      whileHover={{ scale: 1.01 }}
                      className={`flex items-center p-4 rounded-xl border-2 cursor-pointer transition-all ${
                        formData.project_package === pkg.value
                          ? 'border-[#1E3A8A] bg-[#1E3A8A]/5'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name="project_package"
                        value={pkg.value}
                        checked={formData.project_package === pkg.value}
                        onChange={(e) => setFormData({ ...formData, project_package: e.target.value })}
                        className="sr-only"
                      />
                      <div className={`w-5 h-5 rounded-full border-2 mr-4 flex items-center justify-center ${
                        formData.project_package === pkg.value
                          ? 'border-[#1E3A8A] bg-[#1E3A8A]'
                          : 'border-gray-300'
                      }`}>
                        {formData.project_package === pkg.value && (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            className="w-2 h-2 bg-white rounded-full"
                          />
                        )}
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">{pkg.label}</div>
                        <div className="text-sm text-gray-500">{pkg.description}</div>
                      </div>
                    </motion.label>
                  ))}
                </div>
              </FormField>

              {/* Contact Info */}
              <div className="grid md:grid-cols-2 gap-4">
                <FormField label="Your name" required delay={0.1}>
                  <Input
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    placeholder="John Smith"
                    required
                    className="rounded-lg"
                  />
                </FormField>
                
                <FormField label="Email" required delay={0.15}>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="john@example.com"
                    required
                    className="rounded-lg"
                  />
                </FormField>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <FormField label="Phone" delay={0.2}>
                  <Input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="(555) 123-4567"
                    className="rounded-lg"
                  />
                </FormField>
                
                <FormField label="Business name" required delay={0.25}>
                  <Input
                    value={formData.business_name}
                    onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
                    placeholder="Smith's Landscaping"
                    required
                    className="rounded-lg"
                  />
                </FormField>
              </div>

              {/* Business Type */}
              <FormField label="What type of business?" required delay={0.3}>
                <Select
                  value={formData.business_type}
                  onValueChange={(value) => setFormData({ ...formData, business_type: value })}
                >
                  <SelectTrigger className="rounded-lg">
                    <SelectValue placeholder="Select your industry" />
                  </SelectTrigger>
                  <SelectContent>
                    {businessTypes.map(type => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormField>

              {/* Main Goal */}
              <FormField label="What's your main goal with this project?" delay={0.35}>
                <Textarea
                  value={formData.main_goal}
                  onChange={(e) => setFormData({ ...formData, main_goal: e.target.value })}
                  placeholder="e.g., Get more local customers, look more professional online, launch my first ad campaign..."
                  rows={3}
                  className="rounded-lg"
                />
              </FormField>

              {/* Timeline */}
              <FormField label="Timeline" delay={0.4}>
                <Select
                  value={formData.timeline}
                  onValueChange={(value) => setFormData({ ...formData, timeline: value })}
                >
                  <SelectTrigger className="rounded-lg">
                    <SelectValue placeholder="When do you need this?" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="asap">ASAP</SelectItem>
                    <SelectItem value="1-2 weeks">1-2 weeks</SelectItem>
                    <SelectItem value="1 month">Within a month</SelectItem>
                    <SelectItem value="flexible">Flexible</SelectItem>
                  </SelectContent>
                </Select>
              </FormField>

              {/* Additional Notes */}
              <FormField label="Anything else we should know?" delay={0.45}>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Any specific requirements, preferences, or questions..."
                  rows={3}
                  className="rounded-lg"
                />
              </FormField>

              {/* Submit Button */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
              >
                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white py-6 text-lg rounded-xl shadow-lg shadow-blue-500/25"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5 mr-2" />
                        Submit Request
                      </>
                    )}
                  </Button>
                </motion.div>
                <p className="text-center text-sm text-gray-500 mt-4">
                  We'll review your request and get back to you within 24 hours.
                </p>
              </motion.div>
            </form>
          </div>
        </motion.div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="mb-4 md:mb-0">
              <span className="text-xl font-bold bg-gradient-to-r from-white to-blue-200 bg-clip-text text-transparent">
                BM-Assist.co
              </span>
            </div>
            <div className="flex gap-6 text-sm text-gray-400">
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
              <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
              <Link to="/cookies" className="hover:text-white transition-colors">Cookies</Link>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-500 text-sm">
            © {new Date().getFullYear()} BM-Assist.co. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default RequestPage;
